import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import OCGatewayClient, { ConnectionState } from "../client/OCGatewayClient";
import { queryClient as defaultQueryClient } from "./queryClient";

export const gatewayConnectionStateQueryKey = ['gatewayConnectionState'] as const;

const installedGatewayConnectionStateListeners = new WeakSet<QueryClient>();

export const isGatewayConnecting = (connectionState: ConnectionState | null | undefined) => {
    return connectionState === ConnectionState.Connecting || connectionState === ConnectionState.Reconnecting;
};

export function installGatewayConnectionStateQueryListener(queryClient: QueryClient = defaultQueryClient) {
    if (installedGatewayConnectionStateListeners.has(queryClient)) {
        return;
    }

    queryClient.setQueryData(gatewayConnectionStateQueryKey, OCGatewayClient.connectionState$.getValue());

    OCGatewayClient.connectionState$.subscribe((connectionState) => {
        queryClient.setQueryData(gatewayConnectionStateQueryKey, connectionState);
    });

    installedGatewayConnectionStateListeners.add(queryClient);
}

export function useGatewayConnectionStateQuery() {
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: gatewayConnectionStateQueryKey,
        queryFn: async (): Promise<ConnectionState> => {
            return queryClient.getQueryData<ConnectionState>(gatewayConnectionStateQueryKey)
                ?? OCGatewayClient.connectionState$.getValue();
        },
        staleTime: Infinity,
        gcTime: Infinity,
        networkMode: 'always',
        retry: false
    });

    return {
        ...query,
        connectionState: query.data ?? ConnectionState.Connecting,
        isConnecting: isGatewayConnecting(query.data)
    };
}
